/**
 * Help-paneel achter de "?"-knop in de topbalk (18-10-2026, Jordy).
 *
 * - Start tour: de intro-tour opnieuw (components/IntroTour).
 * - Open guide: de Chatter Startup Guide (src/content/chatter-guide/guide.en.md).
 * - Help-assistent: vragen over het dashboard en de chatregels; antwoorden
 *   alleen uit diezelfde handleiding (POST /api/admin/help/ask). De owner ziet
 *   de vragen met naam (Jordy 18-10), dat staat er ook bij.
 *
 * Het gesprek staat alleen in deze browser (sessionStorage), niet op de server
 * behalve de log van de owner.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import { useMij } from '../lib/rechten'
import { GIDS_MD, TOUR_START } from '../lib/tour'
import { Modal } from './Modal'

interface Beurt { role: 'user' | 'assistant'; text: string; fout?: boolean }

const OPSLAG = 'help.gesprek'
const MAX_VRAAG = 500

function laad(): Beurt[] {
  try { return JSON.parse(sessionStorage.getItem(OPSLAG) || '[]') as Beurt[] } catch { return [] }
}
function bewaar(b: Beurt[]) {
  try { sessionStorage.setItem(OPSLAG, JSON.stringify(b.slice(-30))) } catch { /* privé-venster */ }
}

/** Kleine weergave van de handleiding: koppen, lijsten, alinea's, **vet**. */
function vet(tekst: string): ReactNode[] {
  return tekst.split(/(\*\*[^*]+\*\*)/g).map((d, i) =>
    d.startsWith('**') && d.endsWith('**') ? <b key={i}>{d.slice(2, -2)}</b> : d)
}

export function GidsWeergave({ md }: { md: string }) {
  const schoon = md
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/^> For editors:[\s\S]*?(?=\n[^>])/m, '')
  const blokken = schoon.split(/\n\s*\n/).map((b) => b.trim()).filter((b) => b && b !== '---')
  return (
    <div className="gids">
      {blokken.map((b, i) => {
        const kop = /^(#{1,4}) (.+)$/.exec(b)
        if (kop && !b.includes('\n')) {
          const n = kop[1].length
          return n === 1 ? <h2 key={i}>{kop[2]}</h2> : n === 2 ? <h3 key={i}>{kop[2]}</h3> : <h4 key={i}>{kop[2]}</h4>
        }
        const regels = b.split('\n')
        if (regels.every((r) => /^(\s*[-*] |\s*\d+\. |\s{2,})/.test(r))) {
          const items: string[] = []
          for (const r of regels) {
            if (/^\s*([-*]|\d+\.) /.test(r)) items.push(r.replace(/^\s*([-*]|\d+\.) /, ''))
            else items[items.length - 1] += ' ' + r.trim()
          }
          const genummerd = /^\s*\d+\. /.test(regels[0])
          const lijst = items.map((t, j) => <li key={j}>{vet(t)}</li>)
          return genummerd ? <ol key={i}>{lijst}</ol> : <ul key={i}>{lijst}</ul>
        }
        return <p key={i}>{vet(regels.join(' '))}</p>
      })}
    </div>
  )
}

export function HelpPaneel({ onClose }: { onClose: () => void }) {
  const mij = useMij()
  const rol = String(mij.data?.role ?? '').toLowerCase()
  // Een manager komt op de server niet langs de manager-allowlist (src/api/deps.py).
  const magVragen = rol !== '' && rol !== 'manager' && rol !== 'reader'
  const [beurten, setBeurten] = useState<Beurt[]>(laad)
  const [vraag, setVraag] = useState('')
  const [bezig, setBezig] = useState(false)
  const [gidsOpen, setGidsOpen] = useState(false)
  const onder = useRef<HTMLDivElement | null>(null)

  useEffect(() => { bewaar(beurten); onder.current?.scrollIntoView({ block: 'end' }) }, [beurten])

  const stel = async () => {
    const tekst = vraag.trim()
    if (!tekst || bezig) return
    const eerder = beurten.filter((b) => !b.fout).slice(-6).map(({ role, text }) => ({ role, text }))
    setBeurten((b) => [...b, { role: 'user', text: tekst }])
    setVraag('')
    setBezig(true)
    try {
      const r = await api.post<{ answer: string; answered: boolean }>('/api/admin/help/ask',
        { question: tekst, history: eerder })
      setBeurten((b) => [...b, { role: 'assistant', text: r.answer }])
    } catch (e) {
      const melding = e instanceof Error && e.message ? e.message
        : 'The Help assistant is unavailable right now. Try again later or ask your manager.'
      setBeurten((b) => [...b, { role: 'assistant', text: melding, fout: true }])
    } finally {
      setBezig(false)
    }
  }

  // Via een portal naar <body>: de topbalk (sticky + backdrop-filter) wordt
  // anders de container van position:fixed, en dan kreeg de handleiding-modal
  // alleen de hoogte van de topbalk.
  return createPortal(
    <>
      <div className="helpsluier" onClick={onClose} />
      <div className="helppaneel panel" role="dialog" aria-label="Help" data-testid="help-paneel">
        <div className="head">
          <div>
            <h3>Help</h3>
            <p>Answers come from the Chatter Startup Guide.</p>
          </div>
          <div className="r">
            <button className="btn ghost sm" type="button" data-testid="help-start-tour"
                    onClick={() => { onClose(); window.dispatchEvent(new Event(TOUR_START)) }}>
              Start tour
            </button>
            <button className="btn ghost sm" type="button" data-testid="help-open-guide"
                    onClick={() => setGidsOpen(true)}>
              Open guide
            </button>
            <button className="iconbtn sm" type="button" aria-label="Close help" onClick={onClose}>✕</button>
          </div>
        </div>
        {magVragen ? (
          <>
            <div className="helpgesprek" data-testid="help-gesprek">
              {beurten.length ? beurten.map((b, i) => (
                <div key={i} className={`helpbeurt ${b.role}${b.fout ? ' fout' : ''}`}>
                  {/* De assistent zet knopnamen soms in **vet**: als vet tonen, niet als sterretjes. */}
                  {b.role === 'assistant' ? vet(b.text) : b.text}
                </div>
              )) : (
                <div className="mut2 helpleeg">
                  Ask anything about the dashboard, for example "How do I send paid content?" or
                  "When does a chat stop being mine?"
                </div>
              )}
              {bezig ? <div className="helpbeurt assistant mut2">Thinking…</div> : null}
              <div ref={onder} />
            </div>
            <form className="helpvraag" onSubmit={(e) => { e.preventDefault(); void stel() }}>
              <textarea className="inp" rows={2} maxLength={MAX_VRAAG} value={vraag}
                        placeholder="Type your question…" data-testid="help-vraag"
                        onChange={(e) => setVraag(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void stel() }
                        }} />
              <button className="btn primary sm" type="submit" disabled={bezig || !vraag.trim()}
                      data-testid="help-vraag-verstuur">
                Ask
              </button>
            </form>
            <p className="mut2 helpnoot">
              Your questions are visible to the owner. The assistant only knows the guide: it
              never sees fans, models, messages or passwords.
              {beurten.length ? (
                <button className="linkknop" type="button" onClick={() => setBeurten([])}>Clear</button>
              ) : null}
            </p>
          </>
        ) : (
          <p className="mut2 helpnoot">
            The Help assistant is for chatters. Use the tour and the guide above, or ask the owner.
          </p>
        )}
      </div>
      {gidsOpen ? (
        <Modal title="Chatter Startup Guide" width={760} onClose={() => setGidsOpen(false)}>
          <GidsWeergave md={GIDS_MD} />
        </Modal>
      ) : null}
    </>,
    document.body,
  )
}
